import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, FormControl, InputLabel,
  MenuItem, Select, Typography
} from '@mui/material';
import useApi from 'hooks/useApi';

interface Props {
  teamUid: string;
  clientUID?: string;
}

interface FlowStepOption {
  id?: string;
  _id?: string;
  uid: string;
  title: string;
}

const ChildTxnStartStepSettings: React.FC<Props> = ({ teamUid }) => {
  const { getFlowByTeamUid, getTeamDetailsByUid, updateChildTxnStartStep, showToast } = useApi();
  const [steps, setSteps] = useState<FlowStepOption[]>([]);
  const [startStep, setStartStep] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!teamUid) return;
      try {
        setLoading(true);
        const [flowResp, teamResp] = await Promise.all([
          getFlowByTeamUid(teamUid),
          getTeamDetailsByUid(teamUid)
        ]);
        const flowSteps: FlowStepOption[] = flowResp?.data?.steps && Array.isArray(flowResp.data.steps)
          ? flowResp.data.steps
          : [];
        setSteps(flowSteps);

        // Resolve the stored value (populated object or raw _id) to a step uid.
        const raw = teamResp?.data?.childTxnStartStep;
        const rawId = raw && typeof raw === 'object' ? (raw.uid || raw._id || raw.id) : raw;
        const match = flowSteps.find((s) => s.uid === rawId || s._id === rawId || s.id === rawId);
        setStartStep(match ? match.uid : '');
      } catch (err) {
        console.error('Failed to load child start step settings:', err);
        setSteps([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [teamUid]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await updateChildTxnStartStep(teamUid, startStep || null);
      if (res?.success) {
        showToast('Child transaction start step saved', 'success');
      } else {
        showToast(res?.message || 'Failed to save start step', 'error');
      }
    } catch (err) {
      showToast('Something went wrong while saving', 'error');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, bgcolor: '#fafafa', maxWidth: 560 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Child Transaction Start Step
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Choose which step of this process's flow a child transaction begins at. Leave on
        &quot;Inherit&quot; to start the child wherever the parent currently is.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading steps...</Typography>
        </Box>
      ) : (
        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
          <InputLabel>Start Step</InputLabel>
          <Select
            value={steps.some((s) => s.uid === startStep) ? startStep : ''}
            onChange={(e) => setStartStep(e.target.value as string)}
            label="Start Step"
          >
            <MenuItem value="">
              <em>Inherit parent&apos;s current step</em>
            </MenuItem>
            {steps.length === 0 ? (
              <MenuItem disabled>No steps available</MenuItem>
            ) : (
              steps.map((step, idx) => (
                <MenuItem key={step.uid} value={step.uid}>
                  {idx + 1}. {step.title}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default ChildTxnStartStepSettings;
